import React, { useCallback, useState } from 'react';

import { ZonedDate } from '@eika-infrastruktur/date';
import { AttentionMessage } from '@eika/attention-message';
import { Button } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';
import { ErrorLabel } from '@eika/label';

import { EikaModal } from '../../../components/EikaModal';
import { ErrorCardWrapper } from '../../../components/shared/ErrorCardWrapper';
import { useUpdateLoanPaymentDay } from '../../../fetchHooks/useLoans';
import type { AccountId } from '../../../generated-types';
import useTranslation, { format } from '../../../hooks/useTranslation';
import MonthView from './MonthView/MonthView';
import useEditLoanPaymentDay from './hooks/useEditLoanPaymentDay';
import mapPaymentDayPayload from './utils/mapPaymentDayPayload';
import useValidatePaymentDate, { MAX_DAY, MIN_DAY } from './utils/validatePaymentDate';

type Props = {
    accountId: AccountId;
    show: boolean;
    setShow: React.Dispatch<boolean>;
    currentDay: number;
    nextDate: ZonedDate;
    onSubmit: () => void;
};

const EditLoanPaymentDay: React.FC<Props> = ({ accountId, setShow, currentDay, nextDate, show, onSubmit }) => {
    const translated = useTranslation();

    const validatePaymentDate = useValidatePaymentDate(nextDate);
    const { targetMinDay, isPaymentDateInCurrentMonth, canChange } = useEditLoanPaymentDay(nextDate);

    const [newPaymentDay, setNewPaymentDay] = useState<number | undefined>(currentDay);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const updatePaymentDay = useUpdateLoanPaymentDay();

    const closeModal = useCallback(() => {
        if (updatePaymentDay.isSuccess) {
            onSubmit();
        }
        updatePaymentDay.reset();
    }, [onSubmit, updatePaymentDay]);

    const submitOrRetry = useCallback(() => {
        const error = validatePaymentDate(newPaymentDay);
        if (error) {
            setErrorMessage(error);
            return;
        }
        updatePaymentDay.mutate(
            mapPaymentDayPayload(accountId, newPaymentDay, currentDay, isPaymentDateInCurrentMonth),
        );
    }, [validatePaymentDate, newPaymentDay, updatePaymentDay, accountId, currentDay, isPaymentDateInCurrentMonth]);

    const submit = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            submitOrRetry();
        },
        [submitOrRetry],
    );

    const setNewPaymentDayFromDateSelector = (day: number) => {
        setErrorMessage(undefined);
        setNewPaymentDay(day);
    };

    const setNewPaymentDayFromInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setErrorMessage(undefined);
        const value = e.target.valueAsNumber;
        setNewPaymentDay(value);

        if (value < MIN_DAY || value > MAX_DAY) {
            setErrorMessage(format(translated.loanEdit.errors.dayBiggerThanOrLessThan, MIN_DAY, MAX_DAY));
        }
    };

    return (
        <EikaModal
            id="edit-loan-debit-account"
            open={show}
            setOpen={setShow}
            onClose={closeModal}
            timeOut={updatePaymentDay.isSuccess ? 2500 : undefined}
            contentShouldStretch
        >
            {updatePaymentDay.error ? (
                <ErrorCardWrapper
                    isModal
                    defaultError={translated.loanEdit.errors.paymentDayDefaultError}
                    exception={updatePaymentDay.error}
                >
                    <div className="edit-loan__modal-error-actions">
                        <Button onClick={updatePaymentDay.reset} buttonType="link">
                            {translated.cancel}
                        </Button>
                        <ButtonWithSpinner
                            showSpinner={updatePaymentDay.isPending}
                            buttonType="positive"
                            onClick={submitOrRetry}
                        >
                            {translated.tryAgain}
                        </ButtonWithSpinner>
                    </div>
                </ErrorCardWrapper>
            ) : (
                <form className="edit-loan__modal-form" onSubmit={submit}>
                    <div className="edit-loan__modal-title-wrapper">
                        <h2 id="edit-loan-debit-account-title" className="title edit-loan__title">
                            {translated.loanEdit.editDay}
                        </h2>
                        <Input
                            aria-labelledby="edit-loan-debit-account-title"
                            value={newPaymentDay}
                            type="number"
                            max={MAX_DAY}
                            min={MIN_DAY}
                            onChange={setNewPaymentDayFromInput}
                        />
                    </div>

                    <p className="edit-loan__modal-form-description">{translated.loanEdit.chooseDay}</p>
                    <MonthView
                        className="edit-loan__calendar"
                        selected={newPaymentDay}
                        onSelect={setNewPaymentDayFromDateSelector}
                        maxDay={MAX_DAY}
                        minDay={targetMinDay}
                    />
                    {errorMessage && (
                        <ErrorLabel icon={IconTypes.INFO_24} className="edit-loan__message" text={errorMessage} />
                    )}
                    {!canChange && (
                        <AttentionMessage
                            icon={IconTypes.BANDAGED_HEART}
                            type="error"
                            className="edit-loan__info-message"
                            text={translated.loanEdit.cantBeChanged}
                        />
                    )}
                    {isPaymentDateInCurrentMonth && (
                        <AttentionMessage
                            icon={IconTypes.INFO_24}
                            type="info"
                            className="edit-loan__info-message"
                            text={translated.loanEdit.onlyFutureDates}
                        />
                    )}
                    <AttentionMessage
                        icon={IconTypes.INFO_24}
                        type="info"
                        className="edit-loan__edit-info-message"
                        text={translated.loanEdit.editInfoMessage}
                    />
                    <ButtonWithSpinner
                        className="edit-loan__modal-form-submit"
                        showSpinner={updatePaymentDay.isPending}
                        showSuccess={updatePaymentDay.isSuccess}
                        buttonType="positive"
                        type="submit"
                    >
                        {translated.loanEdit.save}
                    </ButtonWithSpinner>
                </form>
            )}
        </EikaModal>
    );
};

export default EditLoanPaymentDay;
